import { FC } from 'react';
import { useLocation } from 'react-router';
import { Flex } from '@chakra-ui/react';
import { Feature } from '@shinetools/pricing-plan-library';

import {
  FeatureFilterField,
  FeatureName,
  TeamMembersRequestStatus,
  UserFilterValues,
} from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import useFeatureFlagsSwitcher from 'common/hooks/useFeatureFlagsSwitcher';
import { useHasSeenNewFeature } from 'common/hooks/useHasSeenNewFeature';
import useTeamRequestsCreatedSubscription from 'common/hooks/useTeamRequestsCreatedSubscription';
import SunshineSquircleIcon from 'components/_core/SunshineSquircleIcon';
import * as accountingRoutes from 'features/Accounting/routes';
import * as bankRoutes from 'features/Bank/routes';
import * as bankTransferRequestsRoutes from 'features/BankTransferRequests/routes';
import * as dashboardRoutes from 'features/Dashboard/routes';
import * as expenseClaimRequestsRoutes from 'features/ExpenseClaimRequests/routes';
import * as insurancesRoutes from 'features/Insurance/routes';
import * as paymentCardsRoutes from 'features/PaymentCards/routes';
import useTeamMembersInvitationQuota from 'features/Team/hooks/useTeamMembersInvitationQuota';
import { useRequests } from 'features/Team/pages/Requests/hooks/useRequests';
import * as teamRoutes from 'features/Team/routes';
import * as toolboxRoutes from 'features/Toolbox/routes';
import * as workRoutes from 'features/Work/routes';

import useCompanyContext from '../../../common/hooks/useCompanyContext';
import { useCompanyProfile } from '../../../common/hooks/useCompanyProfile';
import Indicator from './components/Indicator';
import NavMenuCollapse, { Link } from './components/NavMenuCollapse';
import NavMenuItem from './components/NavMenuItem';
import { NavMenuTeamRequestsDocument } from './hooks/useTeamRequests';
import locales from './locales';

const NavMenu: FC<{ disableBrowsing: boolean }> = ({ disableBrowsing }) => {
  const location = useLocation();
  const { featureFlags } = useFeatureFlagsSwitcher();
  const { companyProfileId } = useCompanyContext();
  const { company } = useCompanyProfile(companyProfileId);

  const {
    pageInfo: { totalCount: teamRequestTotalCount },
  } = useRequests([TeamMembersRequestStatus.Pending], {
    fetchPolicy: 'cache-first',
  });
  const { isFeatureAvailable, plan } = useCurrentPricingPlan();
  const { teamMemberInvitationsQuota } = useTeamMembersInvitationQuota({
    skip: !isFeatureAvailable(Feature.TeamManagement),
  });
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const [hasSeenDashboard, setHasSeenDashboard] =
    useHasSeenNewFeature('DASHBOARD');
  const [hasSeenUrssafEstimation, setHasSeenUrssafEstimation] =
    useHasSeenNewFeature('URSSAF_ESTIMATION');
  useTeamRequestsCreatedSubscription({
    onData: ({ client }) => {
      client.refetchQueries({
        include: [NavMenuTeamRequestsDocument],
      });
    },
  });
  const [hasSeenBankAccounts] = useHasSeenNewFeature('BANK_ACCOUNTS');

  const renderUpgrade = () => {
    if (
      plan?.upgradablePlanIds.length === 0 ||
      !canAccessFeature({ name: FeatureName.CompanyPlan })
    ) {
      return null;
    }

    return (
      <NavMenuItem
        disabled={disableBrowsing}
        icon="user-star"
        label={locales.upgradeLabel}
        to="/upgrade/choice"
      />
    );
  };

  const hasPendingTeamMembersRequests = teamRequestTotalCount > 0;
  const teamMembersRequestsCount = hasPendingTeamMembersRequests
    ? `(${teamRequestTotalCount})`
    : '';

  /**
   * use `includes` to also display it when opening a request
   */
  const isOnTeamRoutes =
    location.pathname.includes(teamRoutes.requests) ||
    location.pathname.includes(teamRoutes.members);

  const membersSubmenu: Link = {
    disabled: disableBrowsing,
    name: locales.team.routes.members,
    to: teamRoutes.members,
  };

  const requestsSubmenu: Link = {
    children: hasPendingTeamMembersRequests ? <Indicator /> : null,
    disabled: disableBrowsing,
    name: locales.formatString(locales.team.routes.requests, {
      count: teamMembersRequestsCount,
    }),
    to: teamRoutes.requests,
  };

  const renderBankMenuItem = () => {
    if (canAccessFeature({ name: FeatureName.Bank })) {
      return (
        <NavMenuCollapse
          disabled={disableBrowsing}
          icon="bank"
          links={[
            {
              disabled: disableBrowsing,
              name: locales.banking.transactions,
              to: bankRoutes.bankOverview,
            },
            {
              children: !hasSeenBankAccounts ? (
                <SunshineSquircleIcon
                  colorScheme="green"
                  marginLeft="space-8"
                  name="sparkles"
                  size="sm"
                />
              ) : null,
              disabled: disableBrowsing,
              name: locales.banking.bankAccounts,
              to: bankRoutes.bankAccounts,
            },
            ...(canAccessFeature({
              name: FeatureName.Payments,
              withChildFeatureOnly: true,
            })
              ? [
                  {
                    disabled: disableBrowsing,
                    name: locales.payments,
                    to: '/payments',
                  },
                ]
              : []),
            ...(canAccessFeature({
              name: FeatureName.Paymentcards,
              withChildFeatureOnly: true,
            })
              ? [
                  {
                    disabled: disableBrowsing,
                    name: locales.creditCard,
                    to: paymentCardsRoutes.root,
                  },
                ]
              : []),
          ]}
          mainRouteName={
            canAccessFeature({ name: FeatureName.Bank })
              ? locales.banking.proBank
              : locales.banking.bank
          }
          toMainRoute={bankRoutes.root}
        />
      );
    } else {
      if (canAccessFeature({ name: FeatureName.BankInfo })) {
        return (
          <NavMenuCollapse
            disabled={disableBrowsing}
            icon="bank"
            links={[
              {
                disabled: disableBrowsing,
                name: locales.banking.transactions,
                to: bankRoutes.bankOverview,
              },
              {
                children: !hasSeenBankAccounts ? (
                  <SunshineSquircleIcon
                    colorScheme="green"
                    marginLeft="space-8"
                    name="sparkles"
                    size="sm"
                  />
                ) : null,
                disabled: disableBrowsing,
                name: locales.banking.bankAccounts,
                to: bankRoutes.bankAccounts,
              },
            ]}
            mainRouteName={locales.banking.bank}
            toMainRoute={bankRoutes.root}
          />
        );
      }
      return (
        <NavMenuItem
          disabled={disableBrowsing}
          icon="bank"
          label={locales.banking.bank}
          to={bankRoutes.root}
        />
      );
    }
  };

  const teamSubmenus =
    teamMemberInvitationsQuota?.limit === 1
      ? [membersSubmenu]
      : [membersSubmenu, requestsSubmenu];

  return (
    <Flex alignItems="stretch" flexDirection="column" marginLeft="space-4">
      {canAccessFeature({
        name: FeatureName.BankTransactions,
        withChildFeatureOnly: true,
      })
        ? renderBankMenuItem()
        : null}

      {canAccessFeature({
        name: FeatureName.Dashboard,
        withChildFeatureOnly: true,
      }) &&
      isFeatureAvailable(Feature.CashflowManagementDashboard) &&
      featureFlags.enableCashFlowManagement ? (
        <NavMenuCollapse
          disabled={disableBrowsing}
          hasNewFeatureIcon={!hasSeenDashboard}
          icon="pie-chart"
          links={[
            {
              disabled: disableBrowsing,
              name: locales.dashboard.routes.overview,
              to: dashboardRoutes.overview,
            },
            {
              disabled: disableBrowsing,
              name: locales.dashboard.routes.treasury,
              to: dashboardRoutes.treasury,
            },
          ]}
          mainRouteName={locales.dashboard.main}
          onClick={() => setHasSeenDashboard(true)}
          toMainRoute={dashboardRoutes.root}
        >
          {!hasSeenDashboard ? (
            <SunshineSquircleIcon
              colorScheme="green"
              marginLeft="space-4"
              name="sparkles"
              size="sm"
            />
          ) : null}
        </NavMenuCollapse>
      ) : null}

      {canAccessFeature({
        name: FeatureName.RequestBanktransfers,
        where: {
          or: [
            { field: FeatureFilterField.User, values: [UserFilterValues.Self] },
          ],
        },
      }) ? (
        <NavMenuItem
          disabled={disableBrowsing}
          icon="payment"
          label={locales.bankTransfers}
          to={bankTransferRequestsRoutes.root}
        />
      ) : null}

      {canAccessFeature({
        name: FeatureName.RequestPaymentcards,
        where: {
          or: [
            { field: FeatureFilterField.User, values: [UserFilterValues.Self] },
          ],
        },
      }) ? (
        <NavMenuItem
          disabled={disableBrowsing}
          icon="credit-card-recto"
          label={locales.creditCard}
          to={paymentCardsRoutes.root}
        />
      ) : null}

      {canAccessFeature({
        name: FeatureName.RequestExpenseclaims,
        where: {
          or: [
            { field: FeatureFilterField.User, values: [UserFilterValues.Self] },
          ],
        },
      }) ? (
        <NavMenuItem
          disabled={disableBrowsing}
          icon="receipt"
          label={locales.expenseClaims}
          to={expenseClaimRequestsRoutes.root}
        />
      ) : null}

      {canAccessFeature({
        name: FeatureName.Invoicing,
        withChildFeatureOnly: true,
      }) ? (
        <NavMenuCollapse
          disabled={disableBrowsing}
          icon="user-list"
          links={[
            {
              disabled: disableBrowsing,
              name: locales.invoices,
              to: workRoutes.invoices,
            },
            {
              disabled: disableBrowsing,
              name: locales.quotes,
              to: workRoutes.quotes,
            },
            {
              disabled: disableBrowsing,
              name: locales.clients,
              to: workRoutes.clients,
            },
            {
              disabled: disableBrowsing,
              name: locales.catalog,
              to: workRoutes.catalog,
            },
          ]}
          mainRouteName={locales.work}
          toMainRoute={workRoutes.root}
        />
      ) : null}

      {!company?.isMicro &&
      canAccessFeature({
        name: FeatureName.Accountingexport,
        withChildFeatureOnly: true,
      }) ? (
        <NavMenuItem
          disabled={disableBrowsing}
          icon="receipt"
          label={locales.accounting.main}
          to={accountingRoutes.root}
        />
      ) : null}

      {company?.isMicro &&
      canAccessFeature({
        name: FeatureName.Accountingexport,
        withChildFeatureOnly: true,
      }) ? (
        <NavMenuCollapse
          disabled={disableBrowsing}
          hasNewFeatureIcon={!hasSeenUrssafEstimation}
          icon="receipt"
          links={[
            {
              disabled: disableBrowsing,
              name: locales.accounting.routes.estimation,
              to: accountingRoutes.estimation,
            },
            {
              disabled: disableBrowsing,
              name: locales.accounting.routes.exports,
              to: accountingRoutes.exports,
            },
          ]}
          mainRouteName={locales.accounting.main}
          onClick={() => setHasSeenUrssafEstimation(true)}
          toMainRoute={accountingRoutes.estimation}
        >
          {!hasSeenUrssafEstimation ? (
            <SunshineSquircleIcon
              colorScheme="green"
              marginLeft="space-4"
              name="sparkles"
              size="sm"
            />
          ) : null}
        </NavMenuCollapse>
      ) : null}

      {canAccessFeature({
        name: FeatureName.Toolbox,
        withChildFeatureOnly: true,
      }) ? (
        <NavMenuCollapse
          disabled={disableBrowsing}
          icon="toolbox"
          links={[
            {
              disabled: disableBrowsing,
              name: locales.offers,
              to: toolboxRoutes.offers,
            },
            {
              disabled: disableBrowsing,
              name: locales.integrations,
              to: toolboxRoutes.integrations,
            },
            {
              disabled: disableBrowsing,
              name: locales.collections,
              to: toolboxRoutes.collections,
            },
          ]}
          mainRouteName={locales.toolbox}
          toMainRoute={toolboxRoutes.root}
        />
      ) : null}

      {canAccessFeature({
        name: FeatureName.Insurance,
        withChildFeatureOnly: true,
      }) ? (
        <NavMenuItem
          disabled={disableBrowsing}
          icon="umbrella"
          label={locales.insurances}
          to={insurancesRoutes.root}
        />
      ) : null}

      {isFeatureAvailable(Feature.TeamManagement) &&
      canAccessFeature({
        name: FeatureName.Team,
        withChildFeatureOnly: true,
      }) ? (
        <NavMenuCollapse
          disabled={disableBrowsing}
          icon="team"
          links={teamSubmenus}
          mainRouteName={locales.team.main}
          toMainRoute={teamRoutes.root}
        >
          {hasPendingTeamMembersRequests && !isOnTeamRoutes ? (
            <Indicator />
          ) : null}
        </NavMenuCollapse>
      ) : null}

      {renderUpgrade()}

      <NavMenuItem
        disabled={disableBrowsing}
        icon="info"
        label={locales.help}
        to="/help"
      />
    </Flex>
  );
};

export default NavMenu;
